import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Select, Typography, Space, Row, Col, App as AntApp, Flex, Alert,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from './utils';

const { Title, Text } = Typography;

type Ponto = { data: string; bytes: number };
type Dados = {
  bucket: string;
  storage_class: string;
  dias: number;
  pontos: Ponto[];
  atual_bytes: number;
  cached?: boolean;
  cached_at?: string;
};

const TiB = 1024 ** 4, GiB = 1024 ** 3, MiB = 1024 ** 2;
const fmtBytes = (b: number) => {
  if (b >= TiB) return (b / TiB).toFixed(2) + ' TB';
  if (b >= GiB) return (b / GiB).toFixed(1) + ' GB';
  if (b >= MiB) return (b / MiB).toFixed(0) + ' MB';
  return b + ' B';
};
const fmtData = (s: string) => {
  const [, m, d] = s.split('-');
  return `${d}/${m}`;
};

// Crescimento do backup do NAS no Glacier (CloudWatch BucketSizeBytes, cache 1h).
// Mesma arquitetura dos Custos AWS: dre-engine delega pra Lambda fora da VPC.
export default function AdminBackupCrescimento() {
  const { message } = AntApp.useApp();
  const [dados, setDados] = useState<Dados | null>(null);
  const [dias, setDias] = useState(90);
  const [loading, setLoading] = useState(true);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/admin/backup-crescimento?dias=${dias}`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setDados(d);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-line */ }, [dias]);

  if (loading || !dados) return <Card loading />;

  const pontos = dados.pontos || [];
  const semDados = pontos.length === 0;
  const atual = dados.atual_bytes || (pontos.length ? pontos[pontos.length - 1].bytes : 0);
  const primeiro = pontos.length ? pontos[0].bytes : 0;
  const crescPeriodo = atual - primeiro;
  const spanDias = pontos.length > 1
    ? Math.max(1, (new Date(pontos[pontos.length - 1].data).getTime() - new Date(pontos[0].data).getTime()) / 86400_000)
    : 1;
  const mediaDia = crescPeriodo / spanDias;
  // crescimento dos últimos ~7 dias
  const idx7 = pontos.length >= 8 ? pontos.length - 8 : 0;
  const cresc7d = atual - (pontos[idx7]?.bytes || primeiro);

  // ── Gráfico de área (SVG, sem dependência externa) ──
  const W = 760, H = 280, pad = 56, padB = 34;
  const maxB = Math.max(...pontos.map(p => p.bytes), 1);
  const n = pontos.length;
  const px = (i: number) => pad + (n <= 1 ? 0 : (i / (n - 1)) * (W - pad - 16));
  const py = (b: number) => (H - padB) - (b / maxB) * (H - padB - 16);
  const linePath = pontos.map((p, i) => `${i === 0 ? 'M' : 'L'}${px(i).toFixed(1)},${py(p.bytes).toFixed(1)}`).join(' ');
  const areaPath = n
    ? `${linePath} L${px(n - 1).toFixed(1)},${H - padB} L${px(0).toFixed(1)},${H - padB} Z`
    : '';
  // rótulos do eixo X (~6 datas distribuídas)
  const nLabels = Math.min(6, n);
  const xLabelIdx = Array.from({ length: nLabels }, (_, k) =>
    nLabels <= 1 ? 0 : Math.round((k / (nLabels - 1)) * (n - 1)));

  return (
    <div>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16} style={{ marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>💾 Backup do NAS (Glacier)</Title>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Crescimento do backup do servidor do escritório no S3 Glacier Deep Archive.
            Via CloudWatch, cache 1h ({dados.cached ? `de ${new Date(dados.cached_at!).toLocaleString('pt-BR')}` : 'atualizado agora'}).
          </Text>
        </div>
        <Space>
          <Select value={dias} onChange={setDias} size="small" style={{ width: 120 }}
            options={[30, 90, 180, 365].map(n => ({ value: n, label: `${n} dias` }))} />
          <Button icon={<ReloadOutlined />} onClick={carregar} size="small">Atualizar</Button>
        </Space>
      </Flex>

      {semDados ? (
        <Alert type="info" showIcon message="Sem dados de armazenamento ainda"
          description="O CloudWatch grava o tamanho do bucket 1x por dia. Se o backup começou há pouco, volte amanhã." />
      ) : (
        <>
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { borderLeft: '4px solid #020D7F' } }}>
                <Text type="secondary" style={{ fontSize: 10 }}>Tamanho atual</Text>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#020D7F' }}>{fmtBytes(atual)}</div>
                <Text type="secondary" style={{ fontSize: 10 }}>{pontos[pontos.length - 1].data}</Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { borderLeft: '4px solid #0930D0' } }}>
                <Text type="secondary" style={{ fontSize: 10 }}>Crescimento no período</Text>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#0930D0' }}>+{fmtBytes(Math.max(0, crescPeriodo))}</div>
                <Text type="secondary" style={{ fontSize: 10 }}>{Math.round(spanDias)} dias</Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { borderLeft: '4px solid #0a7f4a' } }}>
                <Text type="secondary" style={{ fontSize: 10 }}>Média por dia</Text>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#0a7f4a' }}>{fmtBytes(Math.max(0, mediaDia))}</div>
                <Text type="secondary" style={{ fontSize: 10 }}>no período</Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { borderLeft: '4px solid #B07500' } }}>
                <Text type="secondary" style={{ fontSize: 10 }}>Últimos 7 dias</Text>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#B07500' }}>+{fmtBytes(Math.max(0, cresc7d))}</div>
                <Text type="secondary" style={{ fontSize: 10 }}>variação recente</Text>
              </Card>
            </Col>
          </Row>

          <Card size="small" title="Tamanho do backup ao longo do tempo">
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', maxHeight: 320 }}>
              {/* grid horizontal + rótulos Y */}
              {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
                const y = (H - padB) - f * (H - padB - 16);
                return (
                  <g key={i}>
                    <line x1={pad} x2={W - 16} y1={y} y2={y} stroke="#eee" strokeDasharray="3 3" />
                    <text x={pad - 6} y={y + 3} fontSize="9" fill="#888" textAnchor="end">{fmtBytes(f * maxB)}</text>
                  </g>
                );
              })}
              {/* área + linha */}
              <path d={areaPath} fill="#020D7F" fillOpacity={0.10} />
              <path d={linePath} fill="none" stroke="#020D7F" strokeWidth={2} />
              {/* ponto final destacado */}
              {n > 0 && (
                <circle cx={px(n - 1)} cy={py(atual)} r={3.5} fill="#020D7F" />
              )}
              {/* rótulos X */}
              {xLabelIdx.map((i, k) => (
                <text key={k} x={px(i)} y={H - padB + 16} fontSize="9" fill="#888"
                  textAnchor={k === 0 ? 'start' : k === xLabelIdx.length - 1 ? 'end' : 'middle'}>
                  {fmtData(pontos[i].data)}
                </text>
              ))}
            </svg>
            <Text type="secondary" style={{ fontSize: 10 }}>
              Bucket <code>{dados.bucket}</code> · classe {dados.storage_class}
            </Text>
          </Card>
        </>
      )}
    </div>
  );
}
